<div
  class="solo-sheet"
  [ngStyle]="drawerStyles | theme"
  *transloco="let t; prefix: 'components.solo'"
>
  <div [ngClass]="glowClass"></div>

  <app-nav-button
    icon="close"
    (clickEvent)="onCloseClick()"
    [variant]="drawerConfig.closeButtonInverted ? 'secondary' : 'primary'"
    [size]="drawerConfig.closeButtonSize"
    [ngClass]="drawerConfig.closeButtonPosition"
  />

  @if (showRules()) {
    <div
      role="button"
      class="rules-button"
      tabindex="0"
      [ngClass]="rulesButtonPosition"
      [ngStyle]="rulesStyles | theme"
      [style.--nav-button-size]="closeButtonSizeCss"
      [style.--nav-button-icon-size]="closeButtonIconSizeCss"
      (keydown.enter)="onRulesClick()"
      (keydown.space)="onRulesClick()"
      (click)="onRulesClick()"
    >
      {{ t("rules") }}
    </div>
  }

  @if (waiting()) {
    <div class="waiting">
      @if (entry(); as entry) {
        <div class="recap" [ngStyle]="recapStyles | theme">
          <span class="recap-part">
            <app-currency-amount
              [primaryAmount]="entry.primary"
              [secondaryAmount]="entry.secondary"
              [amountStyles]="recapValueStyles"
              iconSizeCss="--icon-small"
            />
            {{ t("entry") }}
          </span>
          @if (prize(); as prize) {
            <span class="recap-part">
              {{ t("win") }}
              <app-currency-amount
                [primaryAmount]="prize.primary"
                [secondaryAmount]="prize.secondary"
                [amountStyles]="recapPrizeStyles"
                iconSizeCss="--icon-small"
              />
            </span>
          }
          <span class="recap-part">
            {{ t("outscore") }}
            <span [ngStyle]="recapValueStyles | theme">{{ target() }}</span>
          </span>
        </div>
      }
      <app-waiting-for-payment-drawer [invoiceCancelled]="invoiceCancelled()" />
    </div>
  } @else {
    <div class="hero">
      <div class="heading" [ngStyle]="targetLabelStyles | theme">
        <app-icon
          icon="target"
          colorCss="--drawer-solo-label-heading-fill"
          sizeCss="--drawer-solo-label-heading-icon-size"
        />
        <span>{{ t("outscore") }}</span>
      </div>
      <div class="stats">
        <app-solo-stat tokens="drawer-solo-stat" [label]="t('target')">
          <div class="target-line">
            <span class="target" [ngStyle]="statValueStyles | theme">{{ target() }}</span>
            <span [ngStyle]="statUnitStyles | theme">{{ t("points-unit") }}</span>
          </div>
        </app-solo-stat>
        @if (prize(); as prize) {
          <app-solo-stat tokens="drawer-solo-stat" [label]="t('win')">
            <app-currency-amount
              [primaryAmount]="prize.primary"
              [secondaryAmount]="prize.secondary"
              [amountStyles]="statPrizeStyles"
            />
          </app-solo-stat>
        }
        @if (entry(); as entry) {
          <app-solo-stat tokens="drawer-solo-stat" [label]="t('entry-title')">
            <app-currency-amount
              [primaryAmount]="entry.primary"
              [secondaryAmount]="entry.secondary"
              [amountStyles]="statValueStyles"
            />
          </app-solo-stat>
        }
      </div>
    </div>

    @if (entry(); as entry) {
      <div class="entry" [ngStyle]="entryPanelStyles | theme">
        <div class="entry-label" [ngStyle]="entryLabelStyles | theme">{{ t("choose-entry") }}</div>

        <div class="slider-row">
          <button
            type="button"
            class="stepper"
            [ngStyle]="stepperStyles | theme"
            [disabled]="paying() || selectedIndex() === 0"
            [attr.aria-label]="t('lower-entry')"
            (click)="onStep(-1)"
          >
            −
          </button>
          <input
            type="range"
            class="slider"
            min="0"
            step="1"
            [max]="sliderMax()"
            [value]="sliderValue()"
            [disabled]="paying() || singleStake()"
            [style.--solo-slider-progress]="progressPercent() + '%'"
            [attr.aria-label]="t('choose-entry')"
            (input)="onSliderInput($event)"
          />
          <button
            type="button"
            class="stepper"
            [ngStyle]="stepperStyles | theme"
            [disabled]="paying() || selectedIndex() === lastIndex()"
            [attr.aria-label]="t('higher-entry')"
            (click)="onStep(1)"
          >
            +
          </button>
        </div>

        <div class="stops">
          @for (stop of stops(); track $index) {
            <span class="stop" [style.margin-left.%]="stop.position">
              <app-currency-amount
                [primaryAmount]="stop.amount"
                [amountStyles]="$index === selectedIndex() ? selectedStopStyles : stopStyles"
              />
            </span>
          }
        </div>

        @if (qrInPanel()) {
          <app-lightning-invoice-panel
            class="invoice-row"
            layout="row"
            [invoice]="selectedInvoice()"
            [showQR]="true"
            [qrWidth]="panelQrWidth"
            (copyClickedEvent)="onCopyClick()"
          />
        }
      </div>
    }

    <div class="checkout" *transloco="let common; prefix: 'components.pay-drawer-common'">
      <ng-template #ruleLine>
        <div [ngStyle]="ruleStyles | theme">
          <span>{{ rule().before }}</span
          ><span [ngStyle]="ruleMultiplierStyles | theme">{{ rule().figure }}</span
          ><span>{{ rule().after }}</span>
        </div>
        @if (quoteFailed()) {
          <div class="quote" [ngStyle]="ruleStyles | theme">{{ t("target-unavailable") }}</div>
        } @else {
          <div class="quote">
            <div class="quote-bar">
              <div class="quote-bar-fill" [style.width.%]="quotePercent()"></div>
            </div>
            <div class="quote-line" [ngStyle]="quoteNoteStyles | theme">
              {{ t("target-shared") }} {{ t("locked-for") }}
              <span [ngStyle]="quoteSecondsStyles | theme">{{
                t("locked-seconds", { seconds: quoteSeconds() })
              }}</span>
            </div>
          </div>
        }
      </ng-template>

      @if (lightning()) {
        @if (showInvoiceActions() && !showQR()) {
          <app-lightning-invoice-panel
            class="invoice"
            [invoice]="selectedInvoice()"
            [showQR]="false"
            [qrWidth]="panelQrWidth"
            (copyClickedEvent)="onCopyClick()"
          />
        }

        <ng-container *ngTemplateOutlet="ruleLine" />
        <app-currency-button
          class="play-button"
          stretch="fill"
          size="medium"
          [prefix]="common('pay-button-prefix')"
          [suffix]="common('pay-button-suffix')"
          [primaryAmount]="entry()?.primary"
          [hasSecondaryCurrency]="hasSecondaryCurrency()"
          [secondaryAmount]="entry()?.secondary"
          [disabled]="payDisabled()"
          [loading]="invoicePending()"
          (clickEvent)="onPayClick()"
        />
      } @else {
        <ng-container *ngTemplateOutlet="ruleLine" />
        <app-currency-button
          class="play-button"
          stretch="fill"
          size="medium"
          [prefix]="common('pay-button-prefix')"
          [suffix]="common('pay-button-suffix')"
          [primaryAmount]="entry()?.primary"
          [hasSecondaryCurrency]="hasSecondaryCurrency()"
          [secondaryAmount]="entry()?.secondary"
          [disabled]="playDisabled()"
          [loading]="paying()"
          (clickEvent)="onPlayClick()"
        />
      }
    </div>
  }
</div>
